import { useState, useMemo, useRef, useEffect } from 'react'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Badge } from '../ui/badge'
import { EmptyState } from '../EmptyState'
import { useAuth } from '@/contexts/AuthContext'
import { apiRequestComplementFields, type TemplateSection } from '@/lib/api'

const MESSAGE_MAX = 2000

type Props = {
  supplierId: string
  campaignSupplierId: string
  campaignName: string
  sections: TemplateSection[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function SupplierComplementRequest({
  supplierId,
  campaignSupplierId,
  campaignName,
  sections,
  open,
  onOpenChange,
  onSuccess,
}: Props) {
  const { logout } = useAuth()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const requestSeqRef = useRef(0)

  const allFieldIds = useMemo(
    () => sections.flatMap((s) => (s.fields ?? []).map((f) => f.id).filter(Boolean)),
    [sections]
  )
  const allChecked = allFieldIds.length > 0 && selectedIds.size === allFieldIds.length
  const messageTooLong = message.length > MESSAGE_MAX
  const firstFieldId = allFieldIds[0]

  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set())
      setMessage('')
      setIsSubmitting(false)
    }
  }, [open])

  // Focus initial fiable : `autoFocus` en prop React ne se déclenche qu'au mount
  // et Radix Dialog gère son propre focus-trap → un simple autoFocus est ignoré
  // au re-open. Le `Checkbox` shadcn du projet n'est pas forwardRef (React 18),
  // donc on cible via `document.getElementById` après un rAF pour laisser Radix
  // installer son portail et son focus-trap.
  useEffect(() => {
    if (!open || !firstFieldId) return
    const raf = requestAnimationFrame(() => {
      document.getElementById(firstFieldId)?.focus()
    })
    return () => cancelAnimationFrame(raf)
  }, [open, firstFieldId])

  function toggleField(fid: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(fid)) next.delete(fid)
      else next.add(fid)
      return next
    })
  }

  function toggleAll() {
    setSelectedIds(allChecked ? new Set() : new Set(allFieldIds))
  }

  async function handleSubmit() {
    if (selectedIds.size === 0 || isSubmitting || messageTooLong) return
    setIsSubmitting(true)
    const seq = ++requestSeqRef.current
    try {
      const result = await apiRequestComplementFields(supplierId, {
        campaignSupplierId,
        fieldIds: Array.from(selectedIds),
        message: message.trim() || undefined,
      })
      if (seq !== requestSeqRef.current) return
      const count = selectedIds.size
      const base =
        count === 1
          ? 'Complément demandé pour 1 champ'
          : `Complément demandé pour ${count} champs`

      // Story 3.2 — toast différencié selon le succès d'envoi email.
      // `emailDelivered: false` n'est pas un échec bloquant : la demande est
      // bien persistée, seul l'email n'a pas pu partir (pas de survey actif,
      // Resend KO, etc.). On utilise `toast.warning` avec `duration: Infinity`
      // pour que Marie voie le détail sans temps limite — sinon la modale se
      // referme et le message défile trop vite.
      if (result.emailDelivered) {
        toast.success(`${base} et email envoyé`)
      } else {
        toast.warning(
          `${base}. Email non envoyé${
            result.emailError ? ` : ${result.emailError}` : ''
          }`,
          { duration: Infinity }
        )
      }
      onSuccess()
      onOpenChange(false)
    } catch (e: unknown) {
      if (seq !== requestSeqRef.current) return
      const msg = e instanceof Error ? e.message : 'Erreur lors de la demande de complément'
      if (/^API error 401\b/.test(msg) || /^Non authentifié/i.test(msg)) {
        logout()
        return
      }
      if (/^API error 404\b/.test(msg)) {
        toast.error('Soumission introuvable — elle a peut-être été supprimée')
        onSuccess()
        onOpenChange(false)
        return
      }
      toast.error(msg)
    } finally {
      if (seq === requestSeqRef.current) setIsSubmitting(false)
    }
  }

  const isEmpty = allFieldIds.length === 0

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && isSubmitting) return
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Demander un complément — {campaignName}</DialogTitle>
          <DialogDescription>
            Sélectionnez les champs à redemander au fournisseur
          </DialogDescription>
        </DialogHeader>

        {isEmpty ? (
          <EmptyState
            icon="❓"
            title="Aucun champ disponible"
            description="Ce template ne contient aucun champ à demander."
          />
        ) : (
          <>
            <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background py-2">
              <Button type="button" variant="ghost" size="sm" onClick={toggleAll}>
                {allChecked ? 'Tout décocher' : 'Tout cocher'}
              </Button>
              <span className="text-sm text-muted-foreground" aria-live="polite">
                {selectedIds.size} champ{selectedIds.size > 1 ? 's' : ''} sélectionné
                {selectedIds.size > 1 ? 's' : ''}
              </span>
            </div>

            <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-2">
              {sections.map((section, sIdx) => (
                <div key={section.id ?? `section-${sIdx}`}>
                  {section.title && (
                    <h4 className="mb-2 text-sm font-medium">{section.title}</h4>
                  )}
                  <div className="space-y-2">
                    {(section.fields ?? [])
                      .filter((f) => f.id)
                      .map((field) => (
                        <div key={field.id} className="flex items-start gap-2">
                          <Checkbox
                            id={field.id}
                            checked={selectedIds.has(field.id)}
                            onCheckedChange={() => toggleField(field.id)}
                          />
                          <Label
                            htmlFor={field.id}
                            className="flex-1 cursor-pointer text-sm leading-tight"
                          >
                            {field.label ?? field.id}
                            {field.required && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                Requis
                              </Badge>
                            )}
                          </Label>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-1">
              <Label htmlFor="complement-message">Message (optionnel)</Label>
              <Textarea
                id="complement-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={MESSAGE_MAX}
                rows={3}
                placeholder="Précisez le contexte de la demande…"
                aria-invalid={messageTooLong || undefined}
              />
              <div
                className={
                  messageTooLong
                    ? 'text-right text-xs text-destructive'
                    : 'text-right text-xs text-muted-foreground'
                }
              >
                {message.length} / {MESSAGE_MAX}
              </div>
            </div>
          </>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {isEmpty ? 'Fermer' : 'Annuler'}
          </Button>
          {!isEmpty && (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={selectedIds.size === 0 || isSubmitting || messageTooLong}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Envoyer la demande
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
